/** @jsxImportSource theme-ui */
import { Box, Container, Flex, Heading, Text } from 'theme-ui';
import { FormikProps } from 'formik';
import { GrayFormikField } from '@/components/GrayFormikField';
import Button from '@/components/Button';
import Error from '@/components/Error';
import {
    BillingFormValues,
    BillingMethodId,
    SELLABLE_LICENSE_PLAN_IDS,
} from './validation';
import {
    BillingCountryOption,
    BillingLicensePlanOption,
    BillingMethodOption,
} from '@/types/types';

interface Props extends FormikProps<BillingFormValues> {
    countries: BillingCountryOption[];
    billingMethods: BillingMethodOption[];
    licensePlans: BillingLicensePlanOption[];
    currentLicensePlanId?: number;
    isLoadingOptions?: boolean;
    saveError?: unknown;
    onCancel?: () => void;
}

const sectionSx = {
    p: 3,
    borderRadius: 'sm',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: 'gray.2',
    flexBasis: '50%',
};

// GrayFormikField renders no validation message for selects, so show it here.
const SelectError: React.FC<{ name: keyof BillingFormValues } & FormikProps<BillingFormValues>> = ({
    name,
    errors,
    touched,
    submitCount,
}) =>
    errors[name] && (touched[name] || submitCount > 0) ? (
        <Text color="danger" mb="2" sx={{ display: 'block' }}>
            {errors[name]}
        </Text>
    ) : null;

// Names used when the plan list could not be loaded, keyed by the ids of the sellable plans.
const FALLBACK_PLAN_NAMES: Record<number, string> = { 2: 'Quarterly', 3: 'Yearly' };

// Sellable plans plus the company's current plan so the form can always be saved. The select must never be
// empty or blank, so a plan that is missing from the API response (or a response that failed) still gets an option.
export const buildPlanOptions = (
    plans: BillingLicensePlanOption[],
    currentLicensePlanId?: number,
    selectedLicensePlanId?: number | '' | string
) => {
    const options = new Map<number, string>();

    plans
        .filter((p) => SELLABLE_LICENSE_PLAN_IDS.includes(p.Id) || p.Id === currentLicensePlanId)
        .forEach((p) => options.set(p.Id, p.Name ?? FALLBACK_PLAN_NAMES[p.Id] ?? `Plan ${p.Id}`));

    SELLABLE_LICENSE_PLAN_IDS.forEach((id) => {
        if (!options.has(id)) options.set(id, FALLBACK_PLAN_NAMES[id] ?? `Plan ${id}`);
    });

    [currentLicensePlanId, selectedLicensePlanId].forEach((raw) => {
        const id = Number(raw);
        if (raw !== undefined && raw !== '' && id > 0 && !options.has(id)) {
            options.set(id, FALLBACK_PLAN_NAMES[id] ?? `Plan ${id}`);
        }
    });

    return Array.from(options, ([value, label]) => ({ value, label })).sort((a, b) => a.value - b.value);
};

const BillingInformationForm: React.FC<Props> = ({
    countries,
    billingMethods,
    licensePlans,
    currentLicensePlanId,
    isLoadingOptions,
    saveError,
    onCancel,
    ...formProps
}) => {
    const { values, isSubmitting, dirty } = formProps;
    const methodId = values.BillingMethodId === '' ? 0 : Number(values.BillingMethodId);

    const methodOptions = billingMethods
        .filter((m) => !values.CountryId || m.Countries?.includes(values.CountryId))
        .map((m) => ({ value: m.Id, label: m.Name }));
    const countryOptions = countries.map((c) => ({
        value: c.Id,
        label: c.Name ? `${c.Name} (${c.Id})` : c.Id,
    }));
    const planOptions = buildPlanOptions(licensePlans, currentLicensePlanId, values.LicensePlanId);

    return (
        <form onSubmit={formProps.handleSubmit} noValidate>
            <Flex
                backgroundColor="background"
                sx={{ flexDirection: 'column', gap: 2, borderRadius: 'sm' }}
            >
                <Flex
                    sx={{
                        gap: 2,
                        '@media only screen and (max-width:60em)': { flexDirection: 'column' },
                    }}
                >
                    <Box sx={sectionSx}>
                        <Heading as="h3" mb="2">
                            Invoice
                        </Heading>
                        <GrayFormikField name="Name" type="text" label="Name *" />
                        <GrayFormikField name="Attention" type="text" label="Attention" />
                        <GrayFormikField
                            name="Email"
                            type="email"
                            label={
                                methodId === BillingMethodId.EmailInvoice
                                    ? 'Invoice email *'
                                    : 'Invoice email'
                            }
                        />
                        <GrayFormikField
                            name="VatRegistrationNumber"
                            type="text"
                            label="VAT registration number (required in Sweden)"
                        />
                        <GrayFormikField
                            name="LicensePlanId"
                            type="select"
                            label="License plan *"
                            placeholder="Select a plan"
                            options={planOptions}
                            isLoading={isLoadingOptions}
                        />
                        <SelectError name="LicensePlanId" {...formProps} />
                        <GrayFormikField
                            name="PaymentTermsDays"
                            type="number"
                            label="Payment terms (days) *"
                        />
                    </Box>
                    <Box sx={sectionSx}>
                        <Heading as="h3" mb="2">
                            Contact
                        </Heading>
                        <GrayFormikField name="FirstName" type="text" label="First name *" />
                        <GrayFormikField name="LastName" type="text" label="Last name *" />
                        <GrayFormikField name="PhoneNumber" type="text" label="Phone number" />
                        <GrayFormikField
                            name="ContactEmail"
                            type="email"
                            label="Contact person email"
                        />
                    </Box>
                </Flex>
                <Flex
                    sx={{
                        gap: 2,
                        '@media only screen and (max-width:60em)': { flexDirection: 'column' },
                    }}
                >
                    <Box sx={sectionSx}>
                        <Heading as="h3" mb="2">
                            Address
                        </Heading>
                        <GrayFormikField name="Street1" type="text" label="Street *" />
                        <GrayFormikField name="Street2" type="text" label="Street 2" />
                        <GrayFormikField name="ZipCode" type="text" label="Zip code *" />
                        <GrayFormikField name="City" type="text" label="City *" />
                        <GrayFormikField
                            name="CountryId"
                            type="select"
                            label="Country *"
                            placeholder="Select a country"
                            options={countryOptions}
                            isLoading={isLoadingOptions}
                        />
                        <SelectError name="CountryId" {...formProps} />
                    </Box>
                    <Box sx={sectionSx}>
                        <Heading as="h3" mb="2">
                            Billing method
                        </Heading>
                        <GrayFormikField
                            name="BillingMethodId"
                            type="select"
                            label="Billing method *"
                            placeholder="Select a billing method"
                            options={methodOptions}
                            isLoading={isLoadingOptions}
                        />
                        <SelectError name="BillingMethodId" {...formProps} />
                        {values.CountryId && methodOptions.length === 0 && !isLoadingOptions ? (
                            <Text color="danger">No billing methods exist for this country</Text>
                        ) : null}
                        <GrayFormikField
                            name="GLN"
                            type="text"
                            label={
                                methodId === BillingMethodId.ElectronicInvoice ? 'GLN *' : 'GLN'
                            }
                        />
                        <GrayFormikField
                            name="ReferenceLine1"
                            type="text"
                            label={
                                methodId === BillingMethodId.ElectronicInvoice
                                    ? 'Reference line 1 *'
                                    : 'Reference line 1'
                            }
                        />
                        <GrayFormikField
                            name="ReferenceLine2"
                            type="text"
                            label={
                                methodId === BillingMethodId.ElectronicInvoice
                                    ? 'Reference line 2 *'
                                    : 'Reference line 2'
                            }
                        />
                    </Box>
                </Flex>
                {saveError ? (
                    <Container variant="error-message">
                        <Error error={saveError as any} />
                    </Container>
                ) : null}
                <Flex p="2" sx={{ width: '100%', gap: 2, justifyContent: 'flex-end' }}>
                    {onCancel ? (
                        <Button type="button" variant="secondary" onClick={onCancel}>
                            Back to company
                        </Button>
                    ) : null}
                    <Button
                        type="submit"
                        isLoading={isSubmitting}
                        disabled={isSubmitting || !dirty}
                    >
                        Save
                    </Button>
                </Flex>
            </Flex>
        </form>
    );
};

export default BillingInformationForm;
